import { ChevronRight } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { getLanguageFromPath, getRouteWithoutLang } from '@/lib/routes';
import { getDigitalSlugName } from '@/lib/digital-shared-i18n';
import { LARGE_PROGRAMS, LARGE_PROGRAMS_ROUTE } from '@/lib/large-programs-page.js';

// Tier-2 language overrides for breadcrumb labels (hi, ro, cs, hu, fi)
const tier2BreadcrumbOverrides: Record<string, Record<string, string>> = {
  home: { hi: 'होम', ro: 'Acasă', cs: 'Domů', hu: 'Kezdőlap', fi: 'Etusivu' },
  services: { hi: 'सेवाएं', ro: 'Servicii', cs: 'Služby', hu: 'Szolgáltatások', fi: 'Palvelut' },
  bpo: { hi: 'BPO और कॉल सेंटर', ro: 'BPO & Call Center', cs: 'BPO & Call Centrum', hu: 'BPO & Call Center', fi: 'BPO & Puhelinkeskus' },
  'digital-studio': { hi: 'डिजिटल फैक्ट्री', ro: 'Fabrică Digitală', cs: 'Digitální továrna', hu: 'Digitális Gyár', fi: 'Digitaalinen Tehdas' },
  industries: { hi: 'उद्योग', ro: 'Industrii', cs: 'Odvětví', hu: 'Iparágak', fi: 'Toimialat' },
  pricing: { hi: 'मूल्य', ro: 'Prețuri', cs: 'Ceník', hu: 'Árak', fi: 'Hinnat' },
  contact: { hi: 'संपर्क', ro: 'Contact', cs: 'Kontakt', hu: 'Kapcsolat', fi: 'Yhteystiedot' },
  about: { hi: 'हमारे बारे में', ro: 'Despre noi', cs: 'O nás', hu: 'Rólunk', fi: 'Meistä' },
  'case-studies': { hi: 'केस स्टडी', ro: 'Studii de Caz', cs: 'Případové studie', hu: 'Esettanulmányok', fi: 'Tapaustutkimukset' },
  'why-morocco': { hi: 'मोरक्को क्यों', ro: 'De ce Maroc', cs: 'Proč Maroko', hu: 'Miért Marokkó', fi: 'Miksi Marokko' },
  careers: { hi: 'करियर', ro: 'Cariere', cs: 'Kariéra', hu: 'Karrier', fi: 'Ura' },
  glossary: { hi: 'शब्दकोश', ro: 'Glosar', cs: 'Glosář', hu: 'Szójegyzék', fi: 'Sanasto' },
  blog: { hi: 'ब्लॉग', ro: 'Blog', cs: 'Blog', hu: 'Blog', fi: 'Blogi' },
};

const breadcrumbLabels: Record<string, Record<string, string>> = {
  home: {
    en: 'Home', fr: 'Accueil', es: 'Inicio', de: 'Startseite', ar: 'الرئيسية',
    it: 'Home', pt: 'Início', nl: 'Home', pl: 'Strona główna', ru: 'Главная',
    zh: '首页', ja: 'ホーム', tr: 'Ana Sayfa', ko: '홈', sv: 'Hem', da: 'Hjem',
  },
  services: {
    en: 'Services', fr: 'Services', es: 'Servicios', de: 'Dienstleistungen', ar: 'الخدمات',
    it: 'Servizi', pt: 'Serviços', nl: 'Diensten', pl: 'Usługi', ru: 'Услуги',
    zh: '服务', ja: 'サービス', tr: 'Hizmetler', ko: '서비스', sv: 'Tjänster', da: 'Tjenester',
  },
  bpo: {
    en: 'BPO & Call Center', fr: 'BPO & Centre d\'Appel', es: 'BPO & Call Center', de: 'BPO & Callcenter', ar: 'BPO ومركز الاتصال',
    it: 'BPO & Call Center', pt: 'BPO & Call Center', nl: 'BPO & Callcenter', pl: 'BPO & Call Center', ru: 'BPO и Колл-центр',
    zh: 'BPO和呼叫中心', ja: 'BPO＆コールセンター', tr: 'BPO & Çağrı Merkezi', ko: 'BPO 및 콜센터', sv: 'BPO & Callcenter', da: 'BPO & Callcenter',
  },
  'digital-studio': {
    en: 'Digital Factory', fr: 'Usine Numérique', es: 'Fábrica Digital', de: 'Digitale Fabrik', ar: 'المصنع الرقمي',
    it: 'Fabbrica Digitale', pt: 'Fábrica Digital', nl: 'Digitale Fabriek', pl: 'Fabryka Cyfrowa', ru: 'Цифровая фабрика',
    zh: '数字工厂', ja: 'デジタルファクトリー', tr: 'Dijital Fabrika', ko: '디지털 팩토리', sv: 'Digital Fabrik', da: 'Digital Fabrik',
  },
  'inbound-customer-support': {
    en: 'Inbound Customer Support', fr: 'Support Client Entrant', es: 'Soporte al Cliente Entrante', de: 'Inbound-Kundensupport', ar: 'دعم العملاء الوارد',
    it: 'Supporto Clienti Inbound', pt: 'Suporte Inbound', nl: 'Inbound Ondersteuning', pl: 'Wsparcie Przychodzące', ru: 'Входящая Поддержка',
    zh: '入站客户支持', ja: 'インバウンドサポート', tr: 'Gelen Müşteri Desteği', ko: '인바운드 고객 지원', sv: 'Inkommande Support', da: 'Indgående Support',
  },
  'inbound-customer-care': {
    en: 'Inbound Customer Care', fr: 'Service Client Entrant', es: 'Atención al Cliente Entrante', de: 'Inbound-Kundenservice', ar: 'خدمة العملاء الواردة',
    it: 'Assistenza Clienti Inbound', pt: 'Atendimento ao Cliente', nl: 'Inbound Klantenservice', pl: 'Obsługa Klienta', ru: 'Входящее Обслуживание',
    zh: '入站客户服务', ja: 'インバウンドカスタマーケア', tr: 'Gelen Müşteri Hizmetleri', ko: '인바운드 고객 관리', sv: 'Inkommande Kundvård', da: 'Indgående Kundeservice',
  },
  'retention-winback': {
    en: 'Retention & Win-Back', fr: 'Fidélisation & Reconquête', es: 'Retención y Recuperación', de: 'Kundenbindung', ar: 'الاحتفاظ واستعادة العملاء',
    it: 'Fidelizzazione & Win-Back', pt: 'Retenção & Reconquista', nl: 'Retentie & Win-Back', pl: 'Retencja & Win-Back', ru: 'Удержание и Возврат',
    zh: '客户保留与挽回', ja: 'リテンション＆ウィンバック', tr: 'Müşteri Tutma', ko: '유지 및 복귀', sv: 'Retention & Återvinning', da: 'Fastholdelse & Win-Back',
  },
  'sdr-appointment-setting': {
    en: 'SDR & Appointment Setting', fr: 'SDR & Prise de RDV', es: 'SDR & Concertación de Citas', de: 'SDR & Terminvereinbarung', ar: 'SDR وحجز المواعيد',
    it: 'SDR & Fissazione Appuntamenti', pt: 'SDR & Agendamento', nl: 'SDR & Afspraken', pl: 'SDR & Umawianie Spotkań', ru: 'SDR & Назначение Встреч',
    zh: 'SDR与预约设置', ja: 'SDR＆アポイント設定', tr: 'SDR & Randevu', ko: 'SDR & 미팅 설정', sv: 'SDR & Bokning', da: 'SDR & Mødebooking',
  },
  'market-research-surveys': {
    en: 'Market Research & Surveys', fr: 'Études de Marché & Enquêtes', es: 'Investigación de Mercado', de: 'Marktforschung & Umfragen', ar: 'أبحاث السوق',
    it: 'Ricerche di Mercato', pt: 'Pesquisa de Mercado', nl: 'Marktonderzoek', pl: 'Badania Rynku', ru: 'Маркетинговые Исследования',
    zh: '市场调研', ja: '市場調査', tr: 'Pazar Araştırması', ko: '시장 조사', sv: 'Marknadsundersökning', da: 'Markedsundersøgelse',
  },
  'digital-engagement': {
    en: 'Digital Engagement', fr: 'Engagement Digital', es: 'Engagement Digital', de: 'Digitales Engagement', ar: 'التفاعل الرقمي',
    it: 'Engagement Digitale', pt: 'Engajamento Digital', nl: 'Digitale Betrokkenheid', pl: 'Zaangażowanie Cyfrowe', ru: 'Цифровое Взаимодействие',
    zh: '数字互动', ja: 'デジタルエンゲージメント', tr: 'Dijital Etkileşim', ko: '디지털 인게이지먼트', sv: 'Digitalt Engagemang', da: 'Digitalt Engagement',
  },
  'trust-safety-compliance': {
    en: 'Trust, Safety & Compliance', fr: 'Confiance, Sécurité & Conformité', es: 'Confianza, Seguridad y Cumplimiento', de: 'Trust, Safety & Compliance', ar: 'الثقة والأمان والامتثال',
    it: 'Trust, Safety & Compliance', pt: 'Confiança, Segurança & Conformidade', nl: 'Trust, Safety & Compliance', pl: 'Bezpieczeństwo i Zgodność', ru: 'Доверие, Безопасность и Соответствие',
    zh: '信任、安全与合规', ja: 'トラスト＆セーフティ', tr: 'Güvenlik & Uyum', ko: '신뢰, 안전 및 규정', sv: 'Säkerhet & Efterlevnad', da: 'Sikkerhed & Compliance',
  },
  'ai-ops-backoffice': {
    en: 'AI Ops & Back Office', fr: 'IA Ops & Back-Office', es: 'AI Ops & Back Office', de: 'AI Ops & Backoffice', ar: 'عمليات الذكاء الاصطناعي',
    it: 'AI Ops & Back Office', pt: 'AI Ops & Back Office', nl: 'AI Ops & Backoffice', pl: 'AI Ops & Back Office', ru: 'AI Ops и Бэк-офис',
    zh: 'AI运营与后台', ja: 'AIオプス＆バックオフィス', tr: 'AI Ops & Arka Ofis', ko: 'AI Ops & 백오피스', sv: 'AI Ops & Backoffice', da: 'AI Ops & Backoffice',
  },
  'multilingual-support': {
    en: 'Multilingual Support', fr: 'Support Multilingue', es: 'Soporte Multilingüe', de: 'Mehrsprachiger Support', ar: 'الدعم متعدد اللغات',
    it: 'Supporto Multilingue', pt: 'Suporte Multilíngue', nl: 'Meertalige Ondersteuning', pl: 'Wsparcie Wielojęzyczne', ru: 'Многоязычная Поддержка',
    zh: '多语言支持', ja: '多言語サポート', tr: 'Çok Dilli Destek', ko: '다국어 지원', sv: 'Flerspråkig Support', da: 'Flersproget Support',
  },
  'order-management': {
    en: 'Order Management', fr: 'Gestion des Commandes', es: 'Gestión de Pedidos', de: 'Bestellmanagement', ar: 'إدارة الطلبات',
    it: 'Gestione Ordini', pt: 'Gestão de Pedidos', nl: 'Orderbeheer', pl: 'Zarządzanie Zamówieniami', ru: 'Управление Заказами',
    zh: '订单管理', ja: '注文管理', tr: 'Sipariş Yönetimi', ko: '주문 관리', sv: 'Orderhantering', da: 'Ordrestyring',
  },
  'billing-collections': {
    en: 'Billing & Collections', fr: 'Facturation & Recouvrement', es: 'Facturación y Cobros', de: 'Abrechnung & Inkasso', ar: 'الفواتير والتحصيل',
    it: 'Fatturazione & Recupero Crediti', pt: 'Faturamento & Cobrança', nl: 'Facturatie & Incasso', pl: 'Rozliczenia & Windykacja', ru: 'Биллинг и Взыскание',
    zh: '账单与催收', ja: '請求＆回収', tr: 'Faturalama & Tahsilat', ko: '청구 및 수금', sv: 'Fakturering & Inkasso', da: 'Fakturering & Inkasso',
  },
  'customer-retention': {
    en: 'Customer Retention', fr: 'Fidélisation Client', es: 'Retención de Clientes', de: 'Kundenbindung', ar: 'الاحتفاظ بالعملاء',
    it: 'Fidelizzazione Clienti', pt: 'Retenção de Clientes', nl: 'Klantbehoud', pl: 'Retencja Klientów', ru: 'Удержание Клиентов',
    zh: '客户保留', ja: '顧客維持', tr: 'Müşteri Tutma', ko: '고객 유지', sv: 'Kundbehållning', da: 'Kundefastholdelse',
  },
  'lead-qualification': {
    en: 'Lead Qualification', fr: 'Qualification de Leads', es: 'Calificación de Leads', de: 'Lead-Qualifizierung', ar: 'تأهيل العملاء المحتملين',
    it: 'Qualificazione Lead', pt: 'Qualificação de Leads', nl: 'Lead Kwalificatie', pl: 'Kwalifikacja Leadów', ru: 'Квалификация Лидов',
    zh: '线索资格审查', ja: 'リード選別', tr: 'Lead Kalifikasyonu', ko: '리드 자격 심사', sv: 'Leadkvalificering', da: 'Leadkvalificering',
  },
  'live-chat-messaging': {
    en: 'Live Chat & Messaging', fr: 'Chat en Direct & Messagerie', es: 'Chat en Vivo y Mensajería', de: 'Live-Chat & Messaging', ar: 'الدردشة الحية والمراسلة',
    it: 'Live Chat & Messaggistica', pt: 'Chat ao Vivo & Mensagens', nl: 'Live Chat & Berichten', pl: 'Czat & Wiadomości', ru: 'Чат и Сообщения',
    zh: '实时聊天与消息', ja: 'ライブチャット', tr: 'Canlı Sohbet', ko: '실시간 채팅', sv: 'Livechatt & Meddelanden', da: 'Livechat & Beskeder',
  },
  'social-media-care': {
    en: 'Social Media Care', fr: 'Service Client Réseaux Sociaux', es: 'Atención en Redes Sociales', de: 'Social-Media-Betreuung', ar: 'خدمة وسائل التواصل',
    it: 'Assistenza Social Media', pt: 'Atendimento em Redes Sociais', nl: 'Social Media Zorg', pl: 'Obsługa Social Media', ru: 'Обслуживание в Соцсетях',
    zh: '社交媒体客服', ja: 'ソーシャルメディアケア', tr: 'Sosyal Medya Desteği', ko: '소셜 미디어 케어', sv: 'Sociala Medier Support', da: 'Sociale Medier Support',
  },
  'customer-success-saas': {
    en: 'Customer Success for SaaS', fr: 'Customer Success SaaS', es: 'Customer Success para SaaS', de: 'Customer Success für SaaS', ar: 'نجاح العملاء SaaS',
    it: 'Customer Success SaaS', pt: 'Customer Success SaaS', nl: 'Customer Success SaaS', pl: 'Customer Success SaaS', ru: 'Customer Success SaaS',
    zh: 'SaaS客户成功', ja: 'SaaS カスタマーサクセス', tr: 'SaaS Customer Success', ko: 'SaaS 고객 성공', sv: 'Customer Success SaaS', da: 'Customer Success SaaS',
  },
  'collections-payment': {
    en: 'Collections & Payment', fr: 'Recouvrement & Paiement', es: 'Cobros y Pagos', de: 'Inkasso & Zahlung', ar: 'التحصيل والدفع',
    it: 'Recupero Crediti & Pagamenti', pt: 'Cobrança & Pagamento', nl: 'Incasso & Betalingen', pl: 'Windykacja & Płatności', ru: 'Взыскание и Оплата',
    zh: '催收与付款', ja: '回収＆支払い', tr: 'Tahsilat & Ödeme', ko: '수금 및 결제', sv: 'Inkasso & Betalning', da: 'Inkasso & Betaling',
  },
  'qa-coaching': {
    en: 'QA & Coaching', fr: 'QA & Coaching', es: 'QA & Coaching', de: 'QA & Coaching', ar: 'ضمان الجودة والتدريب',
    it: 'QA & Coaching', pt: 'QA & Coaching', nl: 'QA & Coaching', pl: 'QA & Coaching', ru: 'QA и Коучинг',
    zh: 'QA与教练', ja: 'QA＆コーチング', tr: 'QA & Koçluk', ko: 'QA & 코칭', sv: 'QA & Coaching', da: 'QA & Coaching',
  },
  'content-moderation': {
    en: 'Content Moderation', fr: 'Modération de Contenu', es: 'Moderación de Contenido', de: 'Content-Moderation', ar: 'إشراف المحتوى',
    it: 'Moderazione Contenuti', pt: 'Moderação de Conteúdo', nl: 'Content Moderatie', pl: 'Moderacja Treści', ru: 'Модерация Контента',
    zh: '内容审核', ja: 'コンテンツモデレーション', tr: 'İçerik Moderasyonu', ko: '콘텐츠 관리', sv: 'Innehållsmoderering', da: 'Indholdsmoderering',
  },
  'finance-accounting': {
    en: 'Finance & Accounting', fr: 'Finance & Comptabilité', es: 'Finanzas y Contabilidad', de: 'Finanzen & Buchhaltung', ar: 'المالية والمحاسبة',
    it: 'Finanza & Contabilità', pt: 'Finanças & Contabilidade', nl: 'Financiën & Boekhouding', pl: 'Finanse & Księgowość', ru: 'Финансы и Бухгалтерия',
    zh: '财务与会计', ja: '財務＆会計', tr: 'Finans & Muhasebe', ko: '재무 & 회계', sv: 'Finans & Redovisning', da: 'Finans & Regnskab',
  },
  'hr-payroll': {
    en: 'HR & Payroll', fr: 'RH & Paie', es: 'RRHH y Nóminas', de: 'HR & Gehaltsabrechnung', ar: 'الموارد البشرية والرواتب',
    it: 'HR & Payroll', pt: 'RH & Folha de Pagamento', nl: 'HR & Salarisadministratie', pl: 'HR & Płace', ru: 'HR и Зарплата',
    zh: '人力资源与薪资', ja: 'HR＆給与', tr: 'İK & Bordro', ko: 'HR & 급여', sv: 'HR & Lönehantering', da: 'HR & Løn',
  },
  'healthcare-bpo': {
    en: 'Healthcare BPO', fr: 'BPO Santé', es: 'BPO Salud', de: 'Healthcare BPO', ar: 'BPO الرعاية الصحية',
    it: 'BPO Sanitario', pt: 'BPO Saúde', nl: 'Healthcare BPO', pl: 'BPO Opieka Zdrowotna', ru: 'BPO Здравоохранение',
    zh: '医疗BPO', ja: 'ヘルスケアBPO', tr: 'Sağlık BPO', ko: '헬스케어 BPO', sv: 'Hälso-BPO', da: 'Sundheds-BPO',
  },
  'logistics-supply-chain': {
    en: 'Logistics & Supply Chain', fr: 'Logistique & Chaîne d\'Approvisionnement', es: 'Logística & Cadena de Suministro', de: 'Logistik & Lieferkette', ar: 'اللوجستيات وسلسلة التوريد',
    it: 'Logistica & Supply Chain', pt: 'Logística & Cadeia de Suprimentos', nl: 'Logistiek & Supply Chain', pl: 'Logistyka & Łańcuch Dostaw', ru: 'Логистика и Цепочки Поставок',
    zh: '物流与供应链', ja: 'ロジスティクス', tr: 'Lojistik & Tedarik Zinciri', ko: '물류 & 공급망', sv: 'Logistik & Leveranskedja', da: 'Logistik & Forsyningskæde',
  },
  'virtual-assistant': {
    en: 'Virtual Assistant', fr: 'Assistant Virtuel', es: 'Asistente Virtual', de: 'Virtueller Assistent', ar: 'المساعد الافتراضي',
    it: 'Assistente Virtuale', pt: 'Assistente Virtual', nl: 'Virtuele Assistent', pl: 'Wirtualny Asystent', ru: 'Виртуальный Ассистент',
    zh: '虚拟助手', ja: 'バーチャルアシスタント', tr: 'Sanal Asistan', ko: '가상 어시스턴트', sv: 'Virtuell Assistent', da: 'Virtuel Assistent',
  },
  'recruitment-staffing': {
    en: 'Recruitment & Staffing', fr: 'Recrutement & Staffing', es: 'Reclutamiento & Staffing', de: 'Rekrutierung & Personal', ar: 'التوظيف والتعيين',
    it: 'Reclutamento & Staffing', pt: 'Recrutamento & Staffing', nl: 'Werving & Personeel', pl: 'Rekrutacja & Staffing', ru: 'Рекрутинг и Подбор',
    zh: '招聘与人员配置', ja: '採用＆スタッフィング', tr: 'İşe Alım & Kadrolama', ko: '채용 & 인력', sv: 'Rekrytering & Bemanning', da: 'Rekruttering & Bemanding',
  },
  'whatsapp-business-support': {
    en: 'WhatsApp Business Support', fr: 'Support WhatsApp Business', es: 'Soporte WhatsApp Business', de: 'WhatsApp Business Support', ar: 'دعم واتساب للأعمال',
    it: 'Supporto WhatsApp Business', pt: 'Suporte WhatsApp Business', nl: 'WhatsApp Business Support', pl: 'WhatsApp Business', ru: 'WhatsApp Business',
    zh: 'WhatsApp商务支持', ja: 'WhatsApp Businessサポート', tr: 'WhatsApp İş Desteği', ko: 'WhatsApp 비즈니스 지원', sv: 'WhatsApp Business Support', da: 'WhatsApp Business Support',
  },
  'phone-support': {
    en: 'Phone Support', fr: 'Support Téléphonique', es: 'Soporte Telefónico', de: 'Telefonischer Support', ar: 'الدعم الهاتفي',
    it: 'Supporto Telefonico', pt: 'Suporte Telefônico', nl: 'Telefonische Ondersteuning', pl: 'Wsparcie Telefoniczne', ru: 'Телефонная Поддержка',
    zh: '电话支持', ja: '電話サポート', tr: 'Telefon Desteği', ko: '전화 지원', sv: 'Telefonsupport', da: 'Telefonsupport',
  },
  '24-7-support': {
    en: '24/7 Support', fr: 'Support 24/7', es: 'Soporte 24/7', de: '24/7 Support', ar: 'دعم على مدار الساعة',
    it: 'Supporto 24/7', pt: 'Suporte 24/7', nl: '24/7 Ondersteuning', pl: 'Wsparcie 24/7', ru: 'Поддержка 24/7',
    zh: '全天候支持', ja: '24時間サポート', tr: '7/24 Destek', ko: '24/7 지원', sv: '24/7 Support', da: '24/7 Support',
  },
  'email-support': {
    en: 'Email Support', fr: 'Support par Email', es: 'Soporte por Email', de: 'E-Mail-Support', ar: 'الدعم عبر البريد الإلكتروني',
    it: 'Supporto Email', pt: 'Suporte por Email', nl: 'E-mail Support', pl: 'Wsparcie Email', ru: 'Поддержка по Email',
    zh: '邮件支持', ja: 'メールサポート', tr: 'E-posta Desteği', ko: '이메일 지원', sv: 'E-postsupport', da: 'E-mailsupport',
  },
  'workforce-management': {
    en: 'Workforce Management', fr: 'Gestion des Effectifs', es: 'Gestión de Personal', de: 'Workforce Management', ar: 'إدارة القوى العاملة',
    it: 'Workforce Management', pt: 'Gestão de Força de Trabalho', nl: 'Workforce Management', pl: 'Zarządzanie Personelem', ru: 'Управление Персоналом',
    zh: '劳动力管理', ja: 'ワークフォース管理', tr: 'İş Gücü Yönetimi', ko: '인력 관리', sv: 'Personalplanering', da: 'Arbejdsstyringstyring',
  },
  'training-onboarding': {
    en: 'Training & Onboarding', fr: 'Formation & Intégration', es: 'Formación y Onboarding', de: 'Training & Onboarding', ar: 'التدريب والتأهيل',
    it: 'Formazione & Onboarding', pt: 'Treinamento & Onboarding', nl: 'Training & Onboarding', pl: 'Szkolenia & Onboarding', ru: 'Обучение и Адаптация',
    zh: '培训与入职', ja: 'トレーニング＆オンボーディング', tr: 'Eğitim & İşe Alıştırma', ko: '교육 & 온보딩', sv: 'Utbildning & Introduktion', da: 'Uddannelse & Onboarding',
  },
  // Digital Studio services
  'quality-engineering': {
    en: 'Quality Engineering', fr: 'Ingénierie Qualité', es: 'Ingeniería de Calidad', de: 'Quality Engineering', ar: 'هندسة الجودة',
    it: 'Quality Engineering', pt: 'Engenharia de Qualidade', nl: 'Quality Engineering', pl: 'Inżynieria Jakości', ru: 'Инженерия Качества',
    zh: '质量工程', ja: '品質エンジニアリング', tr: 'Kalite Mühendisliği', ko: '품질 엔지니어링', sv: 'Kvalitetsteknik', da: 'Kvalitetsteknik',
  },
  'enterprise-portals': {
    en: 'Enterprise Portals', fr: 'Portails d\'Entreprise', es: 'Portales Empresariales', de: 'Unternehmensportale', ar: 'بوابات المؤسسات',
    it: 'Portali Aziendali', pt: 'Portais Corporativos', nl: 'Bedrijfsportalen', pl: 'Portale Korporacyjne', ru: 'Корпоративные Порталы',
    zh: '企业门户', ja: 'エンタープライズポータル', tr: 'Kurumsal Portallar', ko: '엔터프라이즈 포털', sv: 'Företagsportaler', da: 'Virksomhedsportaler',
  },
  ecommerce: {
    en: 'E-commerce', fr: 'E-commerce', es: 'E-commerce', de: 'E-Commerce', ar: 'التجارة الإلكترونية',
    it: 'E-commerce', pt: 'E-commerce', nl: 'E-commerce', pl: 'E-commerce', ru: 'Электронная Коммерция',
    zh: '电子商务', ja: 'Eコマース', tr: 'E-Ticaret', ko: '이커머스', sv: 'E-handel', da: 'E-handel',
  },
  'api-integration': {
    en: 'API & Integration', fr: 'API & Intégration', es: 'API e Integración', de: 'API & Integration', ar: 'API والتكامل',
    it: 'API & Integrazione', pt: 'API & Integração', nl: 'API & Integratie', pl: 'API & Integracja', ru: 'API и Интеграция',
    zh: 'API与集成', ja: 'API＆インテグレーション', tr: 'API & Entegrasyon', ko: 'API & 통합', sv: 'API & Integration', da: 'API & Integration',
  },
  'ui-ux-design': {
    en: 'UI/UX Design', fr: 'Design UI/UX', es: 'Diseño UI/UX', de: 'UI/UX Design', ar: 'تصميم UI/UX',
    it: 'Design UI/UX', pt: 'Design UI/UX', nl: 'UI/UX Design', pl: 'Projektowanie UI/UX', ru: 'UI/UX Дизайн',
    zh: 'UI/UX设计', ja: 'UI/UXデザイン', tr: 'UI/UX Tasarım', ko: 'UI/UX 디자인', sv: 'UI/UX Design', da: 'UI/UX Design',
  },
  'cloud-devops': {
    en: 'Cloud & DevOps', fr: 'Cloud & DevOps', es: 'Cloud & DevOps', de: 'Cloud & DevOps', ar: 'السحابة و DevOps',
    it: 'Cloud & DevOps', pt: 'Cloud & DevOps', nl: 'Cloud & DevOps', pl: 'Cloud & DevOps', ru: 'Cloud & DevOps',
    zh: '云与DevOps', ja: 'クラウド＆DevOps', tr: 'Bulut & DevOps', ko: '클라우드 & DevOps', sv: 'Moln & DevOps', da: 'Cloud & DevOps',
  },
  'growth-marketing': {
    en: 'Growth Marketing', fr: 'Growth Marketing', es: 'Growth Marketing', de: 'Growth Marketing', ar: 'تسويق النمو',
    it: 'Growth Marketing', pt: 'Growth Marketing', nl: 'Growth Marketing', pl: 'Growth Marketing', ru: 'Маркетинг Роста',
    zh: '增长营销', ja: 'グロースマーケティング', tr: 'Büyüme Pazarlaması', ko: '그로스 마케팅', sv: 'Growth Marketing', da: 'Growth Marketing',
  },
  'emerging-tech-ai': {
    en: 'AI & Emerging Tech', fr: 'IA & Technologies Émergentes', es: 'IA y Tecnologías Emergentes', de: 'KI & Neue Technologien', ar: 'الذكاء الاصطناعي والتقنيات الناشئة',
    it: 'IA & Tecnologie Emergenti', pt: 'IA & Tecnologias Emergentes', nl: 'AI & Opkomende Tech', pl: 'AI & Nowe Technologie', ru: 'ИИ и Новые Технологии',
    zh: 'AI与新兴技术', ja: 'AI＆新興テクノロジー', tr: 'AI & Yeni Teknolojiler', ko: 'AI & 신기술', sv: 'AI & Ny Teknik', da: 'AI & Ny Teknologi',
  },
  'data-engineering-bi': {
    en: 'Data Engineering & BI', fr: 'Ingénierie de Données & BI', es: 'Ingeniería de Datos & BI', de: 'Data Engineering & BI', ar: 'هندسة البيانات و BI',
    it: 'Data Engineering & BI', pt: 'Engenharia de Dados & BI', nl: 'Data Engineering & BI', pl: 'Inżynieria Danych & BI', ru: 'Инженерия Данных и BI',
    zh: '数据工程与BI', ja: 'データエンジニアリング＆BI', tr: 'Veri Mühendisliği & BI', ko: '데이터 엔지니어링 & BI', sv: 'Datateknik & BI', da: 'Datateknik & BI',
  },
  'cybersecurity-appsec': {
    en: 'Cybersecurity', fr: 'Cybersécurité', es: 'Ciberseguridad', de: 'Cybersicherheit', ar: 'الأمن السيبراني',
    it: 'Cybersecurity', pt: 'Cibersegurança', nl: 'Cybersecurity', pl: 'Cyberbezpieczeństwo', ru: 'Кибербезопасность',
    zh: '网络安全', ja: 'サイバーセキュリティ', tr: 'Siber Güvenlik', ko: '사이버보안', sv: 'Cybersäkerhet', da: 'Cybersikkerhed',
  },
  'ai-ml-development': {
    en: 'AI/ML Development', fr: 'Développement IA/ML', es: 'Desarrollo IA/ML', de: 'KI/ML-Entwicklung', ar: 'تطوير الذكاء الاصطناعي',
    it: 'Sviluppo AI/ML', pt: 'Desenvolvimento IA/ML', nl: 'AI/ML Ontwikkeling', pl: 'Rozwój AI/ML', ru: 'Разработка AI/ML',
    zh: 'AI/ML开发', ja: 'AI/ML開発', tr: 'AI/ML Geliştirme', ko: 'AI/ML 개발', sv: 'AI/ML-utveckling', da: 'AI/ML-udvikling',
  },
  'staff-augmentation': {
    en: 'Staff Augmentation', fr: 'Augmentation d\'Équipe IT', es: 'Augmentación de Personal', de: 'Staff Augmentation', ar: 'تعزيز الموظفين',
    it: 'Staff Augmentation', pt: 'Staff Augmentation', nl: 'Staff Augmentation', pl: 'Staff Augmentation', ru: 'Аутстаффинг',
    zh: '人员增强', ja: 'スタッフ増強', tr: 'Personel Takviye', ko: '인력 증강', sv: 'Personalförstärkning', da: 'Personaleforstærkning',
  },
  'digital-transformation': {
    en: 'Digital Transformation', fr: 'Transformation Digitale', es: 'Transformación Digital', de: 'Digitale Transformation', ar: 'التحول الرقمي',
    it: 'Trasformazione Digitale', pt: 'Transformação Digital', nl: 'Digitale Transformatie', pl: 'Transformacja Cyfrowa', ru: 'Цифровая Трансформация',
    zh: '数字化转型', ja: 'デジタルトランスフォーメーション', tr: 'Dijital Dönüşüm', ko: '디지털 전환', sv: 'Digital Transformation', da: 'Digital Transformation',
  },
  'outbound-sales': {
    en: 'Outbound Sales', fr: 'Ventes Sortantes', es: 'Ventas Salientes', de: 'Outbound-Vertrieb', ar: 'المبيعات الصادرة',
    it: 'Vendite Outbound', pt: 'Vendas Outbound', nl: 'Outbound Verkoop', pl: 'Sprzedaż Wychodząca', ru: 'Исходящие Продажи',
    zh: '外呼销售', ja: 'アウトバウンドセールス', tr: 'Giden Satış', ko: '아웃바운드 영업', sv: 'Utgående Försäljning', da: 'Udgående Salg',
  },
  'social-media-management': {
    en: 'Social Media', fr: 'Réseaux Sociaux', es: 'Redes Sociales', de: 'Social Media', ar: 'وسائل التواصل',
    it: 'Social Media', pt: 'Mídias Sociais', nl: 'Social Media', pl: 'Media Społecznościowe', ru: 'Соцсети',
    zh: '社交媒体', ja: 'ソーシャルメディア', tr: 'Sosyal Medya', ko: '소셜 미디어', sv: 'Sociala Medier', da: 'Sociale Medier',
  },
  industries: {
    en: 'Industries', fr: 'Secteurs', es: 'Industrias', de: 'Branchen', ar: 'القطاعات',
    it: 'Settori', pt: 'Setores', nl: 'Sectoren', pl: 'Branże', ru: 'Отрасли',
    zh: '行业', ja: '業界', tr: 'Sektörler', ko: '산업', sv: 'Branscher', da: 'Brancher',
  },
  pricing: {
    en: 'Pricing', fr: 'Tarifs', es: 'Precios', de: 'Preise', ar: 'الأسعار',
    it: 'Prezzi', pt: 'Preços', nl: 'Prijzen', pl: 'Cennik', ru: 'Цены',
    zh: '价格', ja: '料金', tr: 'Fiyatlar', ko: '가격', sv: 'Priser', da: 'Priser',
  },
  contact: {
    en: 'Contact', fr: 'Contact', es: 'Contacto', de: 'Kontakt', ar: 'اتصل بنا',
    it: 'Contatto', pt: 'Contato', nl: 'Contact', pl: 'Kontakt', ru: 'Контакт',
    zh: '联系', ja: 'お問い合わせ', tr: 'İletişim', ko: '연락처', sv: 'Kontakt', da: 'Kontakt',
  },
  start: {
    en: 'Request a quote', fr: 'Demande de devis', es: 'Solicitar presupuesto', de: 'Angebot anfordern', ar: 'طلب عرض سعر',
    it: 'Richiedi un preventivo', pt: 'Pedir orçamento', nl: 'Offerte aanvragen', pl: 'Zapytanie ofertowe', ru: 'Запрос предложения',
    zh: '申请报价', ja: '見積もり依頼', tr: 'Teklif iste', ko: '견적 요청', sv: 'Begär offert', da: 'Anmod om tilbud',
  },
  'why-morocco': {
    en: 'Why Morocco', fr: 'Pourquoi le Maroc', es: '¿Por qué Marruecos?', de: 'Warum Marokko', ar: 'لماذا المغرب',
    it: 'Perché il Marocco', pt: 'Por que Marrocos', nl: 'Waarom Marokko', pl: 'Dlaczego Maroko', ru: 'Почему Марокко',
    zh: '为什么选择摩洛哥', ja: 'なぜモロッコ', tr: 'Neden Fas', ko: '왜 모로코인가', sv: 'Varför Marocko', da: 'Hvorfor Marokko',
  },
  'case-studies': {
    en: 'Case Studies', fr: 'Études de Cas', es: 'Casos de Estudio', de: 'Fallstudien', ar: 'دراسات حالة',
    it: 'Casi Studio', pt: 'Estudos de Caso', nl: 'Case Studies', pl: 'Studia Przypadków', ru: 'Кейсы',
    zh: '案例研究', ja: 'ケーススタディ', tr: 'Vaka Çalışmaları', ko: '사례 연구', sv: 'Fallstudier', da: 'Casestudier',
  },
  about: {
    en: 'About', fr: 'À propos', es: 'Acerca de', de: 'Über uns', ar: 'من نحن',
    it: 'Chi siamo', pt: 'Sobre', nl: 'Over ons', pl: 'O nas', ru: 'О нас',
    zh: '关于我们', ja: '会社概要', tr: 'Hakkımızda', ko: '회사소개', sv: 'Om oss', da: 'Om os',
  },
  'roi-calculator': {
    en: 'ROI Calculator', fr: 'Calculateur ROI', es: 'Calculadora ROI', de: 'ROI-Rechner', ar: 'حاسبة العائد',
    it: 'Calcolatore ROI', pt: 'Calculadora ROI', nl: 'ROI Calculator', pl: 'Kalkulator ROI', ru: 'Калькулятор ROI',
    zh: 'ROI计算器', ja: 'ROI計算機', tr: 'ROI Hesaplayıcı', ko: 'ROI 계산기', sv: 'ROI-kalkylator', da: 'ROI-beregner',
  },
  careers: {
    en: 'Careers', fr: 'Carrières', es: 'Carreras', de: 'Karriere', ar: 'وظائف',
    it: 'Carriere', pt: 'Carreiras', nl: 'Carrières', pl: 'Kariera', ru: 'Карьера',
    zh: '职业', ja: 'キャリア', tr: 'Kariyer', ko: '채용', sv: 'Karriär', da: 'Karriere',
  },
  glossary: {
    en: 'Glossary', fr: 'Glossaire', es: 'Glosario', de: 'Glossar', ar: 'المصطلحات',
    it: 'Glossario', pt: 'Glossário', nl: 'Woordenlijst', pl: 'Słownik', ru: 'Глоссарий',
    zh: '术语表', ja: '用語集', tr: 'Sözlük', ko: '용어집', sv: 'Ordlista', da: 'Ordliste',
  },
  insights: {
    en: 'Insights', fr: 'Insights', es: 'Insights', de: 'Einblicke', ar: 'رؤى',
    it: 'Insights', pt: 'Insights', nl: 'Inzichten', pl: 'Analizy', ru: 'Аналитика',
    zh: '洞察', ja: 'インサイト', tr: 'İçgörüler', ko: '인사이트', sv: 'Insikter', da: 'Indsigter',
  },
  solutions: {
    en: 'Solutions', fr: 'Solutions', es: 'Soluciones', de: 'Lösungen', ar: 'الحلول',
    it: 'Soluzioni', pt: 'Soluções', nl: 'Oplossingen', pl: 'Rozwiązania', ru: 'Решения',
    zh: '解决方案', ja: 'ソリューション', tr: 'Çözümler', ko: '솔루션', sv: 'Lösningar', da: 'Løsninger',
  },
  partners: {
    en: 'Partners', fr: 'Partenaires', es: 'Socios', de: 'Partner', ar: 'الشركاء',
    it: 'Partner', pt: 'Parceiros', nl: 'Partners', pl: 'Partnerzy', ru: 'Партнёры',
    zh: '合作伙伴', ja: 'パートナー', tr: 'Ortaklar', ko: '파트너', sv: 'Partners', da: 'Partnere',
  },
  countries: {
    en: 'Countries', fr: 'Pays', es: 'Países', de: 'Länder', ar: 'الدول',
    it: 'Paesi', pt: 'Países', nl: 'Landen', pl: 'Kraje', ru: 'Страны',
    zh: '国家', ja: '国', tr: 'Ülkeler', ko: '국가', sv: 'Länder', da: 'Lande',
  },
  comparisons: {
    en: 'Comparisons', fr: 'Comparaisons', es: 'Comparaciones', de: 'Vergleiche', ar: 'المقارنات',
    it: 'Confronti', pt: 'Comparações', nl: 'Vergelijkingen', pl: 'Porównania', ru: 'Сравнения',
    zh: '比较', ja: '比較', tr: 'Karşılaştırmalar', ko: '비교', sv: 'Jämförelser', da: 'Sammenligninger',
  },
  legal: {
    en: 'Legal', fr: 'Mentions Légales', es: 'Legal', de: 'Rechtliches', ar: 'قانوني',
    it: 'Legale', pt: 'Legal', nl: 'Juridisch', pl: 'Prawne', ru: 'Юридическая',
    zh: '法律', ja: '法的情報', tr: 'Yasal', ko: '법적', sv: 'Juridiskt', da: 'Juridisk',
  },
  'custom-saas': {
    en: 'Custom SaaS', fr: 'SaaS Sur Mesure', es: 'SaaS Personalizado', de: 'Individuelles SaaS', ar: 'SaaS مخصص',
    it: 'SaaS Personalizzato', pt: 'SaaS Personalizado', nl: 'Maatwerk SaaS', pl: 'SaaS na Zamówienie', ru: 'SaaS на заказ',
    zh: '定制SaaS', ja: 'カスタムSaaS', tr: 'Özel SaaS', ko: '맞춤 SaaS', sv: 'Skräddarsydd SaaS', da: 'Tilpasset SaaS',
  },
  'mobile-apps': {
    en: 'Mobile Apps', fr: 'Applications Mobiles', es: 'Apps Móviles', de: 'Mobile Apps', ar: 'تطبيقات الجوال',
    it: 'App Mobile', pt: 'Apps Móveis', nl: 'Mobiele Apps', pl: 'Aplikacje Mobilne', ru: 'Мобильные Приложения',
    zh: '移动应用', ja: 'モバイルアプリ', tr: 'Mobil Uygulamalar', ko: '모바일 앱', sv: 'Mobilappar', da: 'Mobilapps',
  },
  'mobile-development': {
    en: 'Mobile Development', fr: 'Développement Mobile', es: 'Desarrollo Móvil', de: 'Mobile Entwicklung', ar: 'تطوير التطبيقات',
    it: 'Sviluppo Mobile', pt: 'Desenvolvimento Mobile', nl: 'Mobiele Ontwikkeling', pl: 'Rozwój Mobilny', ru: 'Мобильная Разработка',
    zh: '移动开发', ja: 'モバイル開発', tr: 'Mobil Geliştirme', ko: '모바일 개발', sv: 'Mobilutveckling', da: 'Mobiludvikling',
    hi: 'मोबाइल विकास', ro: 'Dezvoltare Mobilă', cs: 'Mobilní Vývoj', hu: 'Mobilfejlesztés', fi: 'Mobiilikehitys',
  },
  'ai-automation': {
    en: 'AI & Automation', fr: 'IA & Automatisation', es: 'IA & Automatización', de: 'KI & Automatisierung', ar: 'الذكاء الاصطناعي',
    it: 'IA & Automazione', pt: 'IA & Automação', nl: 'AI & Automatisering', pl: 'AI & Automatyzacja', ru: 'ИИ и Автоматизация',
    zh: 'AI与自动化', ja: 'AI＆自動化', tr: 'AI & Otomasyon', ko: 'AI & 자동화', sv: 'AI & Automation', da: 'AI & Automatisering',
  },
  'web-development': {
    en: 'Web Development', fr: 'Développement Web', es: 'Desarrollo Web', de: 'Webentwicklung', ar: 'تطوير الويب',
    it: 'Sviluppo Web', pt: 'Desenvolvimento Web', nl: 'Webontwikkeling', pl: 'Tworzenie Stron', ru: 'Веб-разработка',
    zh: 'Web开发', ja: 'Web開発', tr: 'Web Geliştirme', ko: '웹 개발', sv: 'Webbutveckling', da: 'Webudvikling',
  },
  'ui-ux': {
    en: 'UI/UX Design', fr: 'Design UI/UX', es: 'Diseño UI/UX', de: 'UI/UX Design', ar: 'تصميم UI/UX',
    it: 'Design UI/UX', pt: 'Design UI/UX', nl: 'UI/UX Design', pl: 'Projektowanie UI/UX', ru: 'UI/UX Дизайн',
    zh: 'UI/UX设计', ja: 'UI/UXデザイン', tr: 'UI/UX Tasarım', ko: 'UI/UX 디자인', sv: 'UI/UX Design', da: 'UI/UX Design',
  },
  'sla-guarantees': {
    en: 'SLA & Guarantees', fr: 'SLA & Garanties', es: 'SLA y Garantías', de: 'SLA & Garantien', ar: 'اتفاقية مستوى الخدمة',
    it: 'SLA & Garanzie', pt: 'SLA & Garantias', nl: 'SLA & Garanties', pl: 'SLA i Gwarancje', ru: 'SLA и Гарантии',
    zh: 'SLA与保证', ja: 'SLA＆保証', tr: 'SLA & Garantiler', ko: 'SLA 및 보증', sv: 'SLA & Garantier', da: 'SLA & Garantier',
  },
  'technical-support': {
    en: 'Technical Support', fr: 'Support Technique', es: 'Soporte Técnico', de: 'Technischer Support', ar: 'الدعم الفني',
    it: 'Supporto Tecnico', pt: 'Suporte Técnico', nl: 'Technische Ondersteuning', pl: 'Wsparcie Techniczne', ru: 'Техническая Поддержка',
    zh: '技术支持', ja: 'テクニカルサポート', tr: 'Teknik Destek', ko: '기술 지원', sv: 'Teknisk Support', da: 'Teknisk Support',
  },
  'back-office-data-processing': {
    en: 'Back Office & Data Processing', fr: 'Back-Office & Traitement de Données', es: 'Back Office & Procesamiento de Datos', de: 'Backoffice & Datenverarbeitung', ar: 'المكتب الخلفي ومعالجة البيانات',
    it: 'Back Office & Elaborazione Dati', pt: 'Back Office & Processamento de Dados', nl: 'Backoffice & Dataverwerking', pl: 'Back Office & Przetwarzanie Danych', ru: 'Бэк-офис и Обработка Данных',
    zh: '后台与数据处理', ja: 'バックオフィス＆データ処理', tr: 'Arka Ofis & Veri İşleme', ko: '백오피스 & 데이터 처리', sv: 'Backoffice & Databehandling', da: 'Backoffice & Databehandling',
  },
  'chat-email-support': {
    en: 'Chat & Email Support', fr: 'Support Chat & Email', es: 'Soporte por Chat & Email', de: 'Chat- & E-Mail-Support', ar: 'دعم الدردشة والبريد الإلكتروني',
    it: 'Supporto Chat & Email', pt: 'Suporte Chat & Email', nl: 'Chat & E-mail Support', pl: 'Wsparcie Chat & Email', ru: 'Чат и Email Поддержка',
    zh: '聊天与邮件支持', ja: 'チャット＆メールサポート', tr: 'Sohbet & E-posta Desteği', ko: '채팅 & 이메일 지원', sv: 'Chatt & E-postsupport', da: 'Chat & E-mailsupport',
  },
  'customer-support': {
    en: 'Customer Support', fr: 'Support Client', es: 'Soporte al Cliente', de: 'Kundensupport', ar: 'دعم العملاء',
    it: 'Supporto Clienti', pt: 'Suporte ao Cliente', nl: 'Klantenondersteuning', pl: 'Obsługa Klienta', ru: 'Поддержка Клиентов',
    zh: '客户支持', ja: 'カスタマーサポート', tr: 'Müşteri Desteği', ko: '고객 지원', sv: 'Kundsupport', da: 'Kundesupport',
    hi: 'ग्राहक सहायता', ro: 'Suport Clienți', cs: 'Zákaznická Podpora', hu: 'Ügyfélszolgálat', fi: 'Asiakastuki',
  },
  'fraud-detection': {
    en: 'Fraud Detection', fr: 'Détection de Fraude', es: 'Detección de Fraude', de: 'Betrugserkennung', ar: 'كشف الاحتيال',
    it: 'Rilevamento Frodi', pt: 'Detecção de Fraude', nl: 'Fraudedetectie', pl: 'Wykrywanie Oszustw', ru: 'Обнаружение Мошенничества',
    zh: '欺诈检测', ja: '不正検知', tr: 'Dolandırıcılık Tespiti', ko: '사기 탐지', sv: 'Bedrägeridetektering', da: 'Svindelsporing',
    hi: 'धोखाधड़ी पहचान', ro: 'Detectare Fraudă', cs: 'Detekce Podvodů', hu: 'Csalásfelderítés', fi: 'Petostunnistus',
  },
  'compliance-monitoring': {
    en: 'Compliance Monitoring', fr: 'Surveillance de Conformité', es: 'Monitoreo de Cumplimiento', de: 'Compliance-Überwachung', ar: 'مراقبة الامتثال',
    it: 'Monitoraggio Conformità', pt: 'Monitoramento de Conformidade', nl: 'Compliance Monitoring', pl: 'Monitoring Zgodności', ru: 'Мониторинг Соответствия',
    zh: '合规监控', ja: 'コンプライアンス監視', tr: 'Uyum İzleme', ko: '규정 준수 모니터링', sv: 'Efterlevnadsövervakning', da: 'Overvågning af Overholdelse',
    hi: 'अनुपालन निगरानी', ro: 'Monitorizare Conformitate', cs: 'Monitorování Souladu', hu: 'Megfelelőség Felügyelet', fi: 'Vaatimustenmukaisuuden Seuranta',
  },
  'content-review': {
    en: 'Content Review', fr: 'Révision de Contenu', es: 'Revisión de Contenido', de: 'Content-Überprüfung', ar: 'مراجعة المحتوى',
    it: 'Revisione Contenuti', pt: 'Revisão de Conteúdo', nl: 'Content Review', pl: 'Przegląd Treści', ru: 'Проверка Контента',
    zh: '内容审查', ja: 'コンテンツレビュー', tr: 'İçerik İnceleme', ko: '콘텐츠 검토', sv: 'Innehållsgranskning', da: 'Indholdsgennemgang',
    hi: 'सामग्री समीक्षा', ro: 'Revizuire Conținut', cs: 'Kontrola Obsahu', hu: 'Tartalomfelülvizsgálat', fi: 'Sisällön Tarkistus',
  },
  'data-entry-processing': {
    en: 'Data Entry & Processing', fr: 'Saisie & Traitement de Données', es: 'Entrada y Procesamiento de Datos', de: 'Dateneingabe & -verarbeitung', ar: 'إدخال ومعالجة البيانات',
    it: 'Inserimento & Elaborazione Dati', pt: 'Entrada & Processamento de Dados', nl: 'Gegevensinvoer & -verwerking', pl: 'Wprowadzanie & Przetwarzanie Danych', ru: 'Ввод и Обработка Данных',
    zh: '数据录入与处理', ja: 'データ入力＆処理', tr: 'Veri Giriş & İşleme', ko: '데이터 입력 & 처리', sv: 'Datainmatning & Bearbetning', da: 'Dataindtastning & Behandling',
    hi: 'डेटा प्रविष्टि और प्रसंस्करण', ro: 'Introducere & Procesare Date', cs: 'Zadávání & Zpracování Dat', hu: 'Adatbevitel & Feldolgozás', fi: 'Tietojen Syöttö & Käsittely',
  },
  'managed-services': {
    en: 'Managed Services', fr: 'Services Managés', es: 'Servicios Gestionados', de: 'Managed Services', ar: 'الخدمات المدارة',
    it: 'Servizi Gestiti', pt: 'Serviços Gerenciados', nl: 'Managed Services', pl: 'Usługi Zarządzane', ru: 'Управляемые Услуги',
    zh: '托管服务', ja: 'マネージドサービス', tr: 'Yönetilen Hizmetler', ko: '관리 서비스', sv: 'Hanterade Tjänster', da: 'Administrerede Tjenester',
    hi: 'प्रबंधित सेवाएं', ro: 'Servicii Administrate', cs: 'Řízené Služby', hu: 'Kezelt Szolgáltatások', fi: 'Hallinnoidut Palvelut',
  },
};

function getSegmentLabel(segment: string, lang: string, segments?: string[]): string {
  // "digital" segment should use same translations as "digital-studio"
  if (segment === 'digital') {
    const labels = breadcrumbLabels['digital-studio'];
    if (labels) return labels[lang] || labels['en'] || 'Digital Factory';
  }

  // Large programs page: label comes from its single copy source (EN/FR/ES)
  if (segment === LARGE_PROGRAMS_ROUTE) {
    const c = (LARGE_PROGRAMS as Record<string, { crumb?: string }>)[lang] || LARGE_PROGRAMS.en;
    if (c?.crumb) return c.crumb;
  }

  // Check tier-2 overrides first
  const tier2 = tier2BreadcrumbOverrides[segment];
  if (tier2 && tier2[lang]) return tier2[lang];
  
  const labels = breadcrumbLabels[segment];
  if (labels) {
    return labels[lang] || labels['en'] || segment;
  }

  // Check if this is a digital service slug (child of digital-studio OR digital)
  const isDigitalChild = segments && (segments.includes('digital-studio') || segments.includes('digital'));
  if (isDigitalChild) {
    const digitalName = getDigitalSlugName(segment, lang);
    if (digitalName && digitalName !== segment) return digitalName;
  }

  // Fallback: capitalize slug
  return segment.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

export function Breadcrumbs() {
  const location = useLocation();
  const pathname = location.pathname;
  const lang = getLanguageFromPath(pathname);

  const pathWithoutLang = getRouteWithoutLang(pathname);
  const segments = pathWithoutLang.split('/').filter(Boolean);

  if (segments.length === 0) return null;

  interface Breadcrumb {
    label: string;
    path: string;
    isLast?: boolean;
  }

  const breadcrumbs: Breadcrumb[] = [
    { label: getSegmentLabel('home', lang), path: `/${lang}` }
  ];

  let currentPath = '';
  segments.forEach((segment, idx) => {
    currentPath += `/${segment}`;
    breadcrumbs.push({
      label: getSegmentLabel(segment, lang, segments),
      path: `/${lang}${currentPath}`,
      isLast: idx === segments.length - 1
    });
  });

  return (
    <nav className="bg-slate-50 border-b border-slate-200">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex items-center gap-2 text-sm">
          {breadcrumbs.map((crumb, idx) => (
            <div key={idx} className="flex items-center gap-2">
              {idx > 0 && <ChevronRight className="w-4 h-4 text-slate-400" />}
              {crumb.isLast ? (
                <span className="text-slate-900 font-semibold">{crumb.label}</span>
              ) : (
                <Link to={crumb.path} className="text-cyan-700 hover:text-cyan-800 hover:underline">
                  {crumb.label}
                </Link>
              )}
            </div>
          ))}
        </div>
      </div>
    </nav>
  );
}